import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { ArrowLeft, Scale } from "lucide-react";

export default function General() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/auth" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8 transition">
          <ArrowLeft className="w-4 h-4" /> {t.legal?.backToHome || "Retour"}
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 grid place-items-center rounded-xl bg-gradient-to-br from-[#FF0050] to-[#8A2BE2]">
            <Scale className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-3xl font-black font-display">{t.legal?.general || "Politique générale"}</h1>
        </div>
        <div className="prose prose-invert prose-zinc max-w-none space-y-6 text-zinc-300 leading-relaxed">
          <p className="text-sm text-zinc-500">Dernière mise à jour : 29 octobre 2026</p>

          <h2 className="text-xl font-bold text-white">1. Mission de Metazik</h2>
          <p>
            <span translate="no">Metazik</span> est une plateforme sociale et musicale dédiée aux artistes indépendants.
            Notre mission est de donner aux créateurs les outils pour publier, promouvoir et monétiser leur musique
            directement auprès de leur communauté, sans intermédiaire traditionnel.
          </p>

          <h2 className="text-xl font-bold text-white">2. Principes fondamentaux</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Indépendance artistique :</strong> chaque artiste garde le contrôle total de sa musique et de ses prix.</li>
            <li><strong>Transparence financière :</strong> les commissions sont clairement affichées ; les artistes voient exactement ce qu'ils gagnent.</li>
            <li><strong>Communauté respectueuse :</strong> zéro tolérance envers le harcèlement, la haine et le piratage.</li>
            <li><strong>Accessibilité :</strong> la Plateforme est conçue pour fonctionner même avec une connexion limitée (PWA, fichiers optimisés).</li>
            <li><strong>Multilinguisme :</strong> interface disponible en français et en anglais, avec des termes musicaux universels préservés.</li>
          </ul>

          <h2 className="text-xl font-bold text-white">3. Modèle économique</h2>
          <p>
            Sur chaque vente de chanson ou de projet, <strong>l'artiste reçoit 70 % du prix et <span translate="no">Metazik</span> 30 %</strong>.
            Les frais de paiement liés aux retraits des artistes sont pris sur la part de <span translate="no">Metazik</span>.
            Les frais de l'opérateur Mobile Money lors d'un achat sont payés par l'acheteur. Une partie de la part de
            <span translate="no"> Metazik</span> finance le programme d'affiliation (codes promo).
            Aucun abonnement n'est requis pour utiliser la Plateforme en tant que Fan. Le détail figure dans les{" "}
            <Link to="/terms" className="underline">conditions d'utilisation</Link> et les{" "}
            <Link to="/faq" className="underline">questions fréquentes</Link>.
          </p>

          <h2 className="text-xl font-bold text-white">4. Protection anti-piratage</h2>
          <p>
            La Plateforme intègre des mécanismes de protection des contenus :
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Extraits limités (10 à 60 secondes, choisis par l'artiste) pour les chansons non achetées.</li>
            <li>Liens d'écoute temporaires, impossibles à partager.</li>
            <li>Détection d'enregistrement d'écran avec avertissement.</li>
            <li>Watermarking audio invisible pour tracer les fuites.</li>
            <li>Signalement communautaire des contenus piratés.</li>
          </ul>

          <h2 className="text-xl font-bold text-white">5. Modération des contenus</h2>
          <p>
            Les contenus publiés sont soumis à nos règles communautaires. Sont interdits :
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Contenus violant le droit d'auteur (reprises non autorisées, samples non clearés).</li>
            <li>Discours haineux, incitation à la violence, contenu explicite non signalé.</li>
            <li>Spam, contenus trompeurs ou arnaques.</li>
            <li>Usurpation d'identité.</li>
          </ul>
          <p>
            Les signalements sont traités sous 48 heures. Les violations répétées entraînent la suspension définitive du compte.
          </p>

          <h2 className="text-xl font-bold text-white">6. Lives et interactions en temps réel</h2>
          <p>
            Les lives sont accessibles à tous les utilisateurs connectés. Les artistes sont responsables du contenu
            diffusé en direct. <span translate="no">Metazik</span> se réserve le droit d'interrompre un live en cas de violation des règles.
          </p>

          <h2 className="text-xl font-bold text-white">7. Défis musicaux</h2>
          <p>
            Les défis sont créés par les artistes avec un prix et une durée définis. Les participants soumettent
            des vidéos conformes au règlement du défi. Le gagnant est désigné par l'artiste créateur.
            <span translate="no"> Metazik</span> ne garantit pas le versement du prix en cas de litige entre artiste et participant.
          </p>

          <h2 className="text-xl font-bold text-white">8. Évolution de la Plateforme</h2>
          <p>
            <span translate="no">Metazik</span> est en développement actif. De nouvelles fonctionnalités (playlists collaboratives,
            événements payants, marketplace de beats…) seront ajoutées progressivement. Les utilisateurs seront
            informés des changements majeurs par notification.
          </p>

          <h2 className="text-xl font-bold text-white">9. Contact</h2>
          <p>
            Pour toute question relative à cette politique :<br />
            Email : <strong>equipemetazik@gmail.com</strong>
          </p>
        </div>
      </div>
    </div>
  );
}
